import { trackUiError } from './analytics'
import { clearAllTokens, clearImpersonationToken, getEffectiveToken, getImpersonationToken } from './storage'

export class ApiError extends Error {
  status: number
  detail: unknown

  constructor(status: number, detail: unknown) {
    super(typeof detail === 'string' ? detail : `Request failed (${status})`)
    this.status = status
    this.detail = detail
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  isFormData?: boolean
}

/**
 * Fetch wrapper attaching Authorization from whichever token is currently
 * effective (impersonation token if active, else the normal session
 * token) -- org_id is never passed explicitly by the frontend anywhere,
 * matching the backend's own rule that org_id only ever comes from the
 * verified token.
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = getEffectiveToken()
  const headers = new Headers(options.headers)

  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  let body: BodyInit | undefined
  if (options.body !== undefined) {
    if (options.isFormData) {
      body = options.body as FormData
    } else {
      headers.set('Content-Type', 'application/json')
      body = JSON.stringify(options.body)
    }
  }

  let response: Response
  try {
    response = await fetch(path, { ...options, headers, body })
  } catch (err) {
    trackUiError('network')
    throw err
  }

  // A 401 with no token attached just means "not logged in yet" -- the
  // normal response to AuthProvider's initial /auth/me check on every page
  // load, including public pages like /login and /register. Only a 401
  // for a request that DID carry a token means the session is actually
  // invalid/expired and needs clearing + a forced redirect.
  if (response.status === 401 && token) {
    // If the token that just got rejected was an impersonation token, the
    // admin's own session token is still sitting there valid underneath it
    // (impersonation tokens expire after 15 min vs. 30 for a normal
    // session -- see settings.impersonation_token_expire_minutes) --
    // dropping just the impersonation token and reloading falls back to
    // that session, matching what the "Stop impersonating" button does on
    // purpose, rather than force-logging the admin out entirely.
    if (getImpersonationToken()) {
      clearImpersonationToken()
      window.location.reload()
    } else {
      clearAllTokens()
      if (!window.location.pathname.startsWith('/login')) {
        window.location.assign('/login')
      }
    }
    throw new ApiError(401, 'Not authenticated')
  }

  if (!response.ok) {
    let detail: unknown = null
    try {
      detail = (await response.json()).detail
    } catch {
      // no JSON body
    }
    // Error class only; never the detail text.
    if (response.status >= 500) trackUiError('server')
    else if (response.status === 422) trackUiError('validation')
    throw new ApiError(response.status, detail)
  }

  if (response.status === 204) {
    return undefined as T
  }
  return response.json() as Promise<T>
}

/**
 * FastAPI's `detail` is either a plain string or, for a 422 validation
 * error, an array of `{loc, msg, type}` objects -- naively stringifying
 * the latter renders `[object Object],[object Object]`. Handles both,
 * falling back to `fallback` for anything else (network error, empty body).
 */
export function formatApiError(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) return fallback
  const { detail } = err
  if (typeof detail === 'string' && detail) return detail
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => (item && typeof item === 'object' && 'msg' in item ? String(item.msg) : null))
      .filter((msg): msg is string => Boolean(msg))
    if (messages.length > 0) return messages.join('; ')
  }
  return fallback
}

/**
 * Fetches a document (a rendered invoice, an exported CSV) that needs the
 * same bearer-token auth as apiFetch, but returns raw text instead of
 * parsing JSON -- for opening/downloading via a Blob URL rather than
 * displaying inline (a plain `<a href>` can't carry an Authorization
 * header).
 */
export async function fetchTextDocument(path: string): Promise<string> {
  const token = getEffectiveToken()
  const headers = new Headers()
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const response = await fetch(path, { headers })
  if (!response.ok) {
    let detail: unknown = null
    try {
      detail = (await response.json()).detail
    } catch {
      // no JSON body
    }
    throw new ApiError(response.status, detail)
  }
  return response.text()
}

/** Like fetchTextDocument, for a binary file (an .xlsx): returns a Blob. */
export async function fetchBlobDocument(path: string): Promise<Blob> {
  const token = getEffectiveToken()
  const headers = new Headers()
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const response = await fetch(path, { headers })
  if (!response.ok) {
    let detail: unknown = null
    try {
      detail = (await response.json()).detail
    } catch {
      // no JSON body
    }
    throw new ApiError(response.status, detail)
  }
  return response.blob()
}

/** POST a JSON body and get a file back (an exported CSV) as a Blob. */
export async function postForBlob(path: string, body: unknown): Promise<Blob> {
  const token = getEffectiveToken()
  const headers = new Headers({ 'Content-Type': 'application/json' })
  if (token) headers.set('Authorization', `Bearer ${token}`)
  const response = await fetch(path, { method: 'POST', headers, body: JSON.stringify(body) })
  if (!response.ok) {
    let detail: unknown = null
    try {
      detail = (await response.json()).detail
    } catch {
      // no JSON body
    }
    throw new ApiError(response.status, detail)
  }
  return response.blob()
}

export const api = {
  get: <T,>(path: string) => apiFetch<T>(path),
  post: <T,>(path: string, body?: unknown, options?: RequestOptions) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),
  put: <T,>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PUT', body }),
  patch: <T,>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body }),
  delete: <T,>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
  postForm: <T,>(path: string, formData: FormData) =>
    apiFetch<T>(path, { method: 'POST', body: formData, isFormData: true }),
}
